import React from "react";
import {useTranslation} from "react-i18next";
import {Path, useFieldArray, useFormContext} from "react-hook-form";
import {Box, FormHelperText, Grid, useTheme} from "@mui/material";
import {OutlinedAddButton} from "../../shared/components/buttons/OutlinedAddButton";
import {retrieveError} from "../../shared/components/forms/formUtils";
import {TranslatedValuesForm} from "../../shared/technical/i18n/TranslatedValuesForm";
import {outlined, palette} from "../../shared/technical/style/theme";
import {MuiRhfTextField} from "../../shared/components/forms/MuiRhfTextField";
import {SpecialMenu} from "../../shared/components/menu/SpecialMenu";
import {DeleteWithConfirmMenuEntry} from "../../shared/components/menu/DeleteWithConfirmMenuEntry";
import {MuiRhfSelectField} from "../../shared/components/forms/MuiRhfSelectField";
import {ROLE_TYPES} from "../users/Role";
import {Application} from "./Application";

export function ApplicationRolesComponent(props: {
  disabled?: boolean,
}) {
  const name = "roles";
  const {t} = useTranslation();
  const theme = useTheme();
  const {control, formState: {errors}} = useFormContext<Application>();
  const {fields, append, remove} = useFieldArray<Application>({
    control: control,
    name: "roles"
  });

  function removeRole(index: number) {
    console.info("Removing role with index " + index);
    remove(index);
  }

  return (
    <Grid container={true}>
      <Grid item={true} lg={12} sx={{
        display: 'flex',
        alignItems: 'center',
        '& > *': {
          marginRight: theme.spacing(2)
        }
      }}>
        {!props.disabled &&
          <OutlinedAddButton onClick={() => append({} as any)} title={t('ApplicationRoles.btnAddRole')} />
        }
      </Grid>
      {retrieveError(name, errors) &&
        <FormHelperText error={true}>{retrieveError(name, errors)}</FormHelperText>
      }
      <Grid item={true} xs={12} sx={{marginTop: theme.spacing(2),}}>
        <Grid container={true} spacing={3}>
          {fields.map((role, index) =>
            <ApplicationRolesItemComponent index={index} remove={() => removeRole(index)}
                                           disabled={props.disabled}
                                           key={role.id} namePrefix={name} />
          )}
        </Grid>
      </Grid>
    </Grid>
  )
}

export function ApplicationRolesItemComponent<TFieldValues>(props: {
  namePrefix: string,
  index: number,
  remove: () => void,
  disabled?: boolean
}) {
  const theme = useTheme();
  const {t} = useTranslation();

  return (
    <Grid item={true} xs={12} lg={props.disabled ? 6 : 6} key={props.index}>
      <Box sx={{
        ...outlined,
        padding: theme.spacing(2),
        borderColor: props.disabled ? palette.borderColorDisabled : palette.borderColor
      }} style={{borderColor: props.disabled ? palette.borderColorDisabled : palette.borderColor}}>
        <Grid container={true} spacing={3}>
          <Grid item={true} xs={10} xl={5}>
            <MuiRhfTextField
              name={`${props.namePrefix}.${props.index}.name` as Path<TFieldValues>}
              label={t('Application.roles.role')}
              variant={"standard"}
              fullWidth={true}
              disabled={props.disabled}
            />
          </Grid>
          {!props.disabled &&
            <Grid item={true} xs={2} sx={{display: 'flex', alignItems: 'center', marginLeft: 'auto', justifyContent: 'center'}}>
              <SpecialMenu>
                <DeleteWithConfirmMenuEntry onDelete={() => props.remove()} />
              </SpecialMenu>
            </Grid>
          }
          <Grid item={true} xs={12}>
            <TranslatedValuesForm name={`${props.namePrefix}.${props.index}.displayNames`} disabled={props.disabled} />
          </Grid>
          <Grid item={true} xs={10} xl={5}>
            <MuiRhfSelectField
              name={`${props.namePrefix}.${props.index}.type` as Path<TFieldValues>}
              label={t('Application.roles.type')}
              variant={"standard"}
              fullWidth={true}
              disabled={props.disabled}
              list={ROLE_TYPES}
              getListLabel={(item) => t(`ApplicationRoles.types.${item}`)}
              helperText={props.disabled ? undefined : t('ApplicationRoles.typeDescription')}
            />
          </Grid>
        </Grid>
      </Box>
    </Grid>
  )
}
